import { Navigate } from 'react-router-dom';
import { useUserSettings } from '../../utils/use-user-settings';
import { PageConfigItem, pagesConfig } from '../../shared/pages-config';
import { isAbleToSeeUIElement } from '../../utils/roles/client-roles';
import SimplePage from '../../shared/simple-page';
import { isFeatureEnabled } from '../../utils/feature-flags';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export default function RoutesRedirect() {
  const { usersTier, activeLabs, features, ...allRoles } = useUserSettings();

  function canRenderLink(pageConfig: PageConfigItem): boolean {
    if (usersTier.rank < pageConfig.minimumTier) {
      return false;
    }

    const roles = allRoles[pageConfig.roleGroup];

    if (!isAbleToSeeUIElement(roles, pageConfig.requiredPermission)) {
      return false;
    }

    return true;
  }

  const orderedPages: PageConfigItem[] = [
    pagesConfig.users,
    // Directory
    pagesConfig.gpanelContacts,
    pagesConfig.groups,
    pagesConfig.groupTemplates,
    pagesConfig.orgUnits,
    pagesConfig.sharedContacts,
    // Calendars
    pagesConfig.calendars,
    // child pages of resource calendars
    {
      ...pagesConfig.resourceCalendarsLayout,
      link: pagesConfig.resourceCalendarsLayout.link,
    },
    {
      ...pagesConfig.resourceCalendars,
      link: `${pagesConfig.resourceCalendarsLayout.link}/${pagesConfig.resourceCalendars.link}`,
    },
    {
      ...pagesConfig.resourceBuildings,
      link: `${pagesConfig.resourceCalendarsLayout.link}/${pagesConfig.resourceBuildings.link}`,
    },
    {
      ...pagesConfig.resourceFeatures,
      link: `${pagesConfig.resourceCalendarsLayout.link}/${pagesConfig.resourceFeatures.link}`,
    },
    {
      ...pagesConfig.resourceDefaults,
      link: `${pagesConfig.resourceCalendarsLayout.link}/${pagesConfig.resourceDefaults.link}`,
    },
    pagesConfig.resourceTemplates,
    pagesConfig.calendarTransferLogs,
    // Drive
    pagesConfig.driveExplorer,
    pagesConfig.driveSearch,
    pagesConfig.ownershipTransferLogs,
    pagesConfig.sharedDrives,
    // Devices
    pagesConfig.mobileManagement,
    pagesConfig.chromeDevices,
    // Logging
    pagesConfig.logging,
    // Report Center
    pagesConfig.reports,
    pagesConfig.cPanelLogs,
    // Automation
    pagesConfig.rulesEngine,
    pagesConfig.policies,
    pagesConfig.signatureTemplates,
    {
      ...pagesConfig.decomPolicies,
      link: `${pagesConfig.decommissioningLayout.link}/${pagesConfig.decomPolicies.link}`,
    },
    {
      ...pagesConfig.decomSuspendedUsers,
      link: `${pagesConfig.decommissioningLayout.link}/${pagesConfig.decomSuspendedUsers.link}`,
    },
    {
      ...pagesConfig.decomLogs,
      link: `${pagesConfig.decommissioningLayout.link}/${pagesConfig.decomLogs.link}`,
    },
    {
      ...pagesConfig.decommissioningLayout,
      link: pagesConfig.decommissioningLayout.link,
    },
    pagesConfig.driveSweep,
    pagesConfig.contactSync,
    // Import / Export
    pagesConfig.bulkOps,
    pagesConfig.gmailExport,
    ...(isFeatureEnabled('vaultExportReact', features)
      ? [pagesConfig.vaultExport]
      : []),
    pagesConfig.gmailDeletion,
    // Administration
    pagesConfig.driveLabelsManager,
    {
      ...pagesConfig.indexingUsers,
      link: `${pagesConfig.indexing.link}/${pagesConfig.indexingUsers.link}`,
    },
    {
      ...pagesConfig.indexingGroups,
      link: `${pagesConfig.indexing.link}/${pagesConfig.indexingGroups.link}`,
    },
    {
      ...pagesConfig.indexingCalendars,
      link: `${pagesConfig.indexing.link}/${pagesConfig.indexingCalendars.link}`,
    },
    {
      ...pagesConfig.indexingOrgs,
      link: `${pagesConfig.indexing.link}/${pagesConfig.indexingOrgs.link}`,
    },
    {
      ...pagesConfig.indexing,
      link: pagesConfig.indexing.link,
    },
    pagesConfig.labs,
    pagesConfig.roleManager,
    pagesConfig.api,
    {
      ...pagesConfig.setGpanelAdmin,
      link: `${pagesConfig.settings.link}/${pagesConfig.setGpanelAdmin.link}`,
    },
    {
      ...pagesConfig.ipIntrusion,
      link: `${pagesConfig.settings.link}/${pagesConfig.ipIntrusion.link}`,
    },
    {
      ...pagesConfig.secondaryDomains,
      link: `${pagesConfig.settings.link}/${pagesConfig.secondaryDomains.link}`,
    },
    {
      ...pagesConfig.timezone,
      link: `${pagesConfig.settings.link}/${pagesConfig.timezone.link}`,
    },
    {
      ...pagesConfig.customLabeling,
      link: `${pagesConfig.settings.link}/${pagesConfig.customLabeling.link}`,
    },
    {
      ...pagesConfig.googleApiStatus,
      link: `${pagesConfig.settings.link}/${pagesConfig.googleApiStatus.link}`,
    },
    {
      ...pagesConfig.sessionTimeout,
      link: `${pagesConfig.settings.link}/${pagesConfig.sessionTimeout.link}`,
    },
    {
      ...pagesConfig.welcomeTemplates,
      link: `${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}`,
    },
    // Billing
    {
      ...pagesConfig.setGpanelAdmin,
      link: `${pagesConfig.billing.link}/${pagesConfig.setGpanelAdmin.link}`,
    },
    {
      ...pagesConfig.ipIntrusion,
      link: `${pagesConfig.billing.link}/${pagesConfig.ipIntrusion.link}`,
    },
    {
      ...pagesConfig.secondaryDomains,
      link: `${pagesConfig.billing.link}/${pagesConfig.secondaryDomains.link}`,
    },
    {
      ...pagesConfig.billing,
      link: pagesConfig.billing.link,
    },
  ];

  for (const page of orderedPages) {
    if (
      page.value === 'gpanelContacts' &&
      !activeLabs.includes('gpanelcontacts')
    ) {
      continue;
    }
    if (canRenderLink(page)) {
      return <Navigate to={page.link} replace />;
    }
  }

  return (
    <SimplePage title="Welcome" pagesConfigKey="users" showBookmark={false}>
      <Stack
        sx={{
          height: '100%',
          width: '100%',
        }}
        justifyContent="center"
        alignItems="center"
        gap={2}
      >
        <Typography variant="h5">No accessible pages</Typography>
        <Typography>
          You do not have permission to view any pages. Please contact an
          administrator.
        </Typography>
      </Stack>
    </SimplePage>
  );
}
